import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import moment from "moment";
import { db } from "../firebaseConfig";
import {
  restoreSeriesStateFields,
  sanitizeJobDates,
  stripSeriesStateFields,
} from "./recurringOccurrenceUtils";

const RADIO_CIRCLE_STYLE = (selected) => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  border: selected ? "6px solid #1d4ed8" : "2px solid #94a3b8",
  background: "#fff",
  flexShrink: 0,
  boxSizing: "border-box",
  transition: "border 0.1s ease",
});

const RADIO_OPTION_STYLE = (selected) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  borderRadius: 10,
  border: selected ? "1px solid #1d4ed8" : "1px solid #e2e8f0",
  background: selected ? "#eff6ff" : "#fff",
  cursor: "pointer",
  fontSize: 15,
  fontWeight: 600,
  color: "#0f172a",
});

const RecurringEditScopeDialog = ({ onSelect }) => {
  const [choice, setChoice] = useState(null);

  const options = [
    { key: "occurrence", label: "Edit this job only" },
    { key: "future", label: "Edit this job and all future jobs" },
  ];

  return (
    <div
      onClick={() => onSelect("cancel")}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.55)",
        zIndex: 1999998,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
          width: "min(440px, 92vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          background: "#fff",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
          textAlign: "left",
          boxSizing: "border-box",
          zIndex: 1999999,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Edit Recurring Job</h3>
        <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
          This job repeats. Choose how this edit should apply.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map((opt) => (
            <div
              key={opt.key}
              role="radio"
              aria-checked={choice === opt.key}
              tabIndex={0}
              style={RADIO_OPTION_STYLE(choice === opt.key)}
              onClick={() => setChoice(opt.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setChoice(opt.key);
              }}
            >
              <span style={RADIO_CIRCLE_STYLE(choice === opt.key)} />
              {opt.label}
            </div>
          ))}
        </div>

        <div className="button-group" style={{ marginTop: 20 }}>
          <button
            className="blue-button"
            disabled={!choice}
            style={!choice ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            onClick={() => choice && onSelect(choice)}
          >
            Confirm
          </button>
          <button className="red-button" onClick={() => onSelect("cancel")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// The single source of truth for "when does this recurring series stop
// generating occurrences". Any page that expands a recurring job into
// calendar days MUST cap its loop with this value, or a job whose series
// was split/ended (see saveRecurringJobEdit's "future" mode and
// endRecurringSeries below) will keep rendering past its real end date and
// overlap with whatever replaced it - which is what caused duplicate tiles
// on the Calendar page.
export const getRecurrenceEndKey = (job) => {
  const recurrence = typeof job?.recurrence === "object" ? job.recurrence : {};
  const raw = recurrence.endsOn || recurrence.endDate || job?.recurrenceEndDate;
  if (!raw) return null;
  const m = moment(raw);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

// Dates an occurrence has been individually closed/removed for, without
// touching the rest of the series or its history.
export const getExcludedDateSet = (job) => {
  const excluded = Array.isArray(job?.excludedDates) ? job.excludedDates : [];
  return new Set(
    excluded
      .map((d) => (moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : null))
      .filter(Boolean)
  );
};

const CloseJobScopeDialog = ({ onSelect }) => {
  const [choice, setChoice] = useState(null);

  const options = [
    {
      key: "occurrence",
      label: "Close this job only (this date)",
      hint: "Removes just this occurrence. The rest of the series is unaffected.",
    },
    {
      key: "future",
      label: "Close this job and all future jobs",
      hint: "Stops future occurrences but keeps all past history intact.",
    },
    {
      key: "all",
      label: "Delete all job history",
      hint: "Permanently deletes this job and every occurrence's history. This cannot be undone.",
    },
  ];

  return (
    <div
      onClick={() => onSelect("cancel")}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.55)",
        zIndex: 1999998,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
          width: "min(460px, 92vw)",
          maxHeight: "85vh",
          overflowY: "auto",
          background: "#fff",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
          textAlign: "left",
          boxSizing: "border-box",
          zIndex: 1999999,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>Close Recurring Job</h3>
        <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14 }}>
          This job repeats. Choose how this should apply.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {options.map((opt) => (
            <div
              key={opt.key}
              role="radio"
              aria-checked={choice === opt.key}
              tabIndex={0}
              style={RADIO_OPTION_STYLE(choice === opt.key)}
              onClick={() => setChoice(opt.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setChoice(opt.key);
              }}
            >
              <span style={RADIO_CIRCLE_STYLE(choice === opt.key)} />
              <span>
                <div>{opt.label}</div>
                <div style={{ fontWeight: 400, fontSize: 12.5, color: "#64748b", marginTop: 2 }}>
                  {opt.hint}
                </div>
              </span>
            </div>
          ))}
        </div>

        <div className="button-group" style={{ marginTop: 20 }}>
          <button
            className={choice === "all" ? "red-button" : "blue-button"}
            disabled={!choice}
            style={!choice ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            onClick={() => choice && onSelect(choice)}
          >
            Confirm
          </button>
          <button className="grey-button" onClick={() => onSelect("cancel")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export const isRecurringJobRecord = (job) => {
  const type =
    typeof job?.recurrence === "object"
      ? job?.recurrence?.type
      : job?.recurrence || job?.reccurance || job?.recurring || job?.repeat;

  return Boolean(type && String(type).toLowerCase().trim() !== "none");
};

export const getRecurringOccurrenceKey = (job) =>
  moment(
    job?.originalOccurrenceDate ||
      job?.occurrenceDate ||
      job?.__renderDate ||
      job?.startDate
  ).format("YYYY-MM-DD");

export const chooseRecurringEditScope = () =>
  new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const finish = (scope) => {
      root.unmount();
      container.remove();
      resolve(scope);
    };

    root.render(<RecurringEditScopeDialog onSelect={finish} />);
  });

export const chooseCloseJobScope = () =>
  new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const finish = (scope) => {
      root.unmount();
      container.remove();
      resolve(scope);
    };

    root.render(<CloseJobScopeDialog onSelect={finish} />);
  });

export const cleanRecurringEditPayload = (editedJob = {}) => {
  const payload = { ...editedJob };

  [
    "id",
    "__renderDate",
    "occurrenceDate",
    "originalOccurrenceDate",
    "occurrenceStatus",
    "companyUseMode",
    "newFiles",
    "tempCustomDate",
    "__rowKey",
    "__occurrenceRow",
    "__seriesRow",
  ].forEach((key) => delete payload[key]);

  // Every page's save button routes through here, so this is the one place
  // that has to enforce the endDate rule. Nothing should ever be able to
  // persist a recurring job whose endDate looks like a multi-day span.
  //
  // stripSeriesStateFields() drops completedDates / invoicedDates /
  // excludedDates / completedAtByDate / notes / occurrenceOverrides. Those are
  // series-level bookkeeping the app maintains itself - they are never typed
  // into the edit form, so echoing them back on save only ever clobbers newer
  // values or (in the "occurrence" branch below) freezes a stale copy inside
  // an override that then shadows the live document.
  return stripSeriesStateFields(sanitizeJobDates(payload));
};

export const applyOccurrenceOverrideToRenderedJob = (renderedJob) => {
  if (!renderedJob || !isRecurringJobRecord(renderedJob)) return renderedJob;

  const originalOccurrenceDate = moment(
    renderedJob.originalOccurrenceDate ||
      renderedJob.occurrenceDate ||
      renderedJob.__renderDate ||
      renderedJob.startDate
  ).format("YYYY-MM-DD");

  const override = renderedJob.occurrenceOverrides?.[originalOccurrenceDate];

  if (!override) return renderedJob;

  const originalRenderDate = moment(
    renderedJob.__renderDate || originalOccurrenceDate
  );
  const originalStart = moment(originalOccurrenceDate);
  const movedStart = moment(
    override.startDate || override.occurrenceDate || originalOccurrenceDate
  );

  const dayOffset = originalRenderDate.diff(originalStart, "days");
  const movedRenderDate = movedStart.clone().add(dayOffset, "days");

  // ⚠️ restoreSeriesStateFields() is not optional.
  //
  // `override` is a snapshot of the whole job taken when the user edited this
  // one date. Overrides saved before cleanRecurringEditPayload started
  // stripping them still carry a frozen completedDates/invoicedDates from that
  // moment. Spreading them here would shadow the LIVE arrays on renderedJob,
  // so an occurrence marked complete AFTER being rescheduled kept rendering as
  // open - no red on Calendar, still listed on Home. Always take these fields
  // from the document, never from the override.
  return restoreSeriesStateFields(
    {
      ...renderedJob,
      ...override,
      id: renderedJob.id,
      recurrence: renderedJob.recurrence,
      originalOccurrenceDate,
      occurrenceDate: movedStart.format("YYYY-MM-DD"),
      startDate: movedStart.format("YYYY-MM-DD"),
      __renderDate: movedRenderDate.format("YYYY-MM-DD"),
      isOccurrenceOverride: true,
    },
    renderedJob
  );
};


export const getOccurrenceOverrideRenderJobs = (masterJob) => {
  if (!masterJob || !isRecurringJobRecord(masterJob)) return [];

  const overrides = masterJob.occurrenceOverrides || {};

  // ⚠️ DO NOT derive a span from masterJob.startDate -> masterJob.endDate.
  //
  // On a RECURRING job those two fields describe the SERIES, not a single
  // occurrence's duration. Spanning them here meant a job starting 8/3 with an
  // (auto-filled) endDate of 8/31 emitted 29 render copies of the SAME
  // occurrence - the exact duplication users were seeing on Home/Calendar.
  //
  // A recurring occurrence is one day. The only thing that can widen it is an
  // explicit endDate on THAT specific override, set deliberately by a user
  // editing this one date.
  const defaultSpanDays = 0;

  return Object.entries(overrides).flatMap(
    ([originalOccurrenceDate, override]) => {
      const movedStart = moment(
        override?.startDate ||
          override?.occurrenceDate ||
          originalOccurrenceDate
      );

      if (!movedStart.isValid()) return [];

      const overrideEnd = override?.endDate ? moment(override.endDate) : null;
      const spanDays =
        overrideEnd && overrideEnd.isValid() && overrideEnd.isAfter(movedStart, "day")
          ? Math.max(overrideEnd.diff(movedStart, "days"), 0)
          : defaultSpanDays;

      return Array.from({ length: spanDays + 1 }, (_, dayOffset) =>
        // Same reason as in applyOccurrenceOverrideToRenderedJob: the override
        // may carry a frozen completedDates/invoicedDates. The master doc is
        // the only source of truth for those.
        restoreSeriesStateFields(
          {
            ...masterJob,
            ...override,
            id: masterJob.id,
            recurrence: masterJob.recurrence,
            originalOccurrenceDate,
            occurrenceDate: movedStart.format("YYYY-MM-DD"),
            startDate: movedStart.format("YYYY-MM-DD"),
            // Single-day occurrence => endDate IS the occurrence date. Never
            // inherit a stale series-level endDate here, or downstream
            // jobSpansDate()/jobSpansRange() checks will match this one
            // occurrence on every day up to that far-off date.
            endDate:
              spanDays > 0
                ? movedStart.clone().add(spanDays, "days").format("YYYY-MM-DD")
                : movedStart.format("YYYY-MM-DD"),
            __renderDate: movedStart
              .clone()
              .add(dayOffset, "days")
              .format("YYYY-MM-DD"),
            isOccurrenceOverride: true,
          },
          masterJob
        )
      );
    }
  );
};

export const mergeRenderedRecurringJobs = (jobs = []) => {
  const map = new Map();

  const normalizeDateKey = (value) => {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  };

  const getSeriesKey = (job) => {
    if (!isRecurringJobRecord(job)) {
      return String(job?.id || job?.wo || "job").trim().toLowerCase();
    }

    const recurrenceType =
      typeof job?.recurrence === "object"
        ? job.recurrence?.type
        : job?.recurrence || job?.reccurance || job?.recurring || job?.repeat;

    return [
      job?.wo || job?.workOrder || job?.workOrderNumber || "",
      job?.siteName || job?.property || "",
      job?.serviceType || "",
      normalizeDateKey(job?.originalSeriesStartDate || job?.seriesStartDate || job?.startDate),
      recurrenceType || "",
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .join("|");
  };

  jobs.forEach((job) => {
    if (!job) return;

    const originalOccurrenceKey = normalizeDateKey(
      job.originalOccurrenceDate ||
        job.occurrenceDate ||
        job.__renderDate ||
        job.startDate
    );
    const renderDateKey = normalizeDateKey(job.__renderDate || job.startDate);
    const key = [getSeriesKey(job), originalOccurrenceKey, renderDateKey].join("__");

    // Override render jobs are the most specific representation of an
    // occurrence, so prefer them when both normal and override paths produce
    // the same calendar tile.
    const existing = map.get(key);
    if (!existing || job.isOccurrenceOverride) {
      map.set(key, job);
    }
  });

  return Array.from(map.values());
};

export const saveRecurringJobEdit = async ({
  originalJob,
  editedJob,
}) => {
  if (!originalJob?.id || !editedJob) {
    throw new Error("A job and edited job are required.");
  }

  const payload = cleanRecurringEditPayload(editedJob);
  const recurring = isRecurringJobRecord(originalJob);

  if (!recurring) {
    await updateDoc(doc(db, "jobs", originalJob.id), payload);

    return {
      mode: "single",
      updatedJob: { ...originalJob, ...payload, id: originalJob.id },
    };
  }

  const scope = await chooseRecurringEditScope();

  if (scope === "cancel") {
    return { mode: "cancel" };
  }

  const occurrenceKey = getRecurringOccurrenceKey(originalJob);

  if (scope === "occurrence") {
    const jobRef = doc(db, "jobs", originalJob.id);
    const snap = await getDoc(jobRef);
    const current = snap.exists() ? snap.data() : originalJob;

    const occurrenceOverrides = {
      ...(current.occurrenceOverrides || {}),
      [occurrenceKey]: {
        ...payload,
        originalOccurrenceDate: occurrenceKey,
        occurrenceDate: payload.startDate || occurrenceKey,
        startDate: payload.startDate || occurrenceKey,
        updatedAt: new Date().toISOString(),
      },
    };

    await updateDoc(jobRef, { occurrenceOverrides });

    return {
      mode: "occurrence",
      occurrenceKey,
      updatedJob: {
        ...originalJob,
        ...occurrenceOverrides[occurrenceKey],
        occurrenceOverrides,
        id: originalJob.id,
        originalOccurrenceDate: occurrenceKey,
        isOccurrenceOverride: true,
      },
      masterJob: {
        ...current,
        occurrenceOverrides,
        id: originalJob.id,
      },
    };
  }

  const oldJobRef = doc(db, "jobs", originalJob.id);
  const oldSnap = await getDoc(oldJobRef);
  const oldJob = oldSnap.exists() ? oldSnap.data() : originalJob;

  const oldRecurrence =
    typeof oldJob.recurrence === "object"
      ? { ...oldJob.recurrence }
      : { type: oldJob.recurrence || "none" };

  const originalSeriesEnd = oldRecurrence.endsOn || "";
  const splitStart = moment(payload.startDate || occurrenceKey).format("YYYY-MM-DD");
  const oldSeriesEnd = moment(occurrenceKey)
    .subtract(1, "day")
    .format("YYYY-MM-DD");

  await updateDoc(oldJobRef, {
    recurrence: {
      ...oldRecurrence,
      endsOn: oldSeriesEnd,
    },
    seriesSplitAfter: oldSeriesEnd,
    updatedAt: serverTimestamp(),
  });

  const newSeriesData = {
    ...oldJob,
    ...payload,
    startDate: splitStart,
    recurrence: {
      ...oldRecurrence,
      ...(payload.recurrence || {}),
      endsOn:
        payload?.recurrence?.endsOn !== undefined
          ? payload.recurrence.endsOn
          : originalSeriesEnd,
    },
    seriesRootId: oldJob.seriesRootId || originalJob.id,
    previousSeriesId: originalJob.id,
    originalWO: oldJob.wo || "",
    wo: oldJob.wo || "",
    occurrenceOverrides: {},
    completedDates: [],
    invoicedDates: [],
    excludedDates: [],
    completedAtByDate: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  delete newSeriesData.id;
  delete newSeriesData.__renderDate;
  delete newSeriesData.occurrenceDate;
  delete newSeriesData.originalOccurrenceDate;
  delete newSeriesData.occurrenceStatus;

  const newDocRef = await addDoc(collection(db, "jobs"), newSeriesData);

  return {
    mode: "future",
    oldJob: {
      ...oldJob,
      recurrence: {
        ...oldRecurrence,
        endsOn: oldSeriesEnd,
      },
      id: originalJob.id,
    },
    newJob: {
      ...newSeriesData,
      id: newDocRef.id,
    },
  };
};

// Shared "Close Job" action for every page that offers a delete/close
// button. Non-recurring jobs get a plain confirm and a real delete, exactly
// like before. Recurring jobs get the scope dialog:
//   - "occurrence": closes just this date (adds it to excludedDates) -
//     equivalent to "delete this one job only, this recurring date".
//   - "future": ends the series after this occurrence (sets
//     recurrence.endsOn) WITHOUT deleting the document or any past
//     history - this is "end the recurring job without deleting its
//     history" / "delete this job and future jobs".
//   - "all": permanently deletes the job document and all of its history.
export const closeOrDeleteJob = async ({ job, collectionName = "jobs" }) => {
  if (!job?.id) {
    throw new Error("A job is required.");
  }

  const recurring = isRecurringJobRecord(job);

  if (!recurring) {
    const confirmed = window.confirm(
      "Close out this job? This will permanently delete it and cannot be undone."
    );
    if (!confirmed) return { mode: "cancel" };

    await deleteDoc(doc(db, collectionName, job.id));
    return { mode: "deleted", jobId: job.id };
  }

  const scope = await chooseCloseJobScope();
  if (scope === "cancel") return { mode: "cancel" };

  const jobRef = doc(db, collectionName, job.id);

  if (scope === "all") {
    await deleteDoc(jobRef);
    return { mode: "deleted", jobId: job.id };
  }

  const occurrenceKey = getRecurringOccurrenceKey(job);
  const snap = await getDoc(jobRef);
  const current = snap.exists() ? snap.data() : job;

  if (scope === "occurrence") {
    const excludedDates = Array.from(
      new Set([...(current.excludedDates || []), occurrenceKey])
    );

    await updateDoc(jobRef, { excludedDates, updatedAt: serverTimestamp() });

    return {
      mode: "occurrence",
      occurrenceKey,
      updatedJob: { ...job, ...current, excludedDates, id: job.id },
    };
  }

  // scope === "future": stop the series after this occurrence, keep history.
  const currentRecurrence =
    typeof current.recurrence === "object"
      ? { ...current.recurrence }
      : { type: current.recurrence || "none" };

  const newEnd = moment(occurrenceKey).subtract(1, "day").format("YYYY-MM-DD");
  const existingEnd = currentRecurrence.endsOn
    ? moment(currentRecurrence.endsOn)
    : null;

  // If the series was already set to end earlier than this occurrence,
  // don't push the end date later.
  const finalEnd =
    existingEnd && existingEnd.isValid() && existingEnd.isBefore(moment(newEnd), "day")
      ? currentRecurrence.endsOn
      : newEnd;

  const updatedRecurrence = { ...currentRecurrence, endsOn: finalEnd };

  await updateDoc(jobRef, {
    recurrence: updatedRecurrence,
    seriesEndedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });

  return {
    mode: "future",
    updatedJob: {
      ...job,
      ...current,
      recurrence: updatedRecurrence,
      id: job.id,
    },
  };
};
